import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {BasicTableProps} from 'notebook/structured-tables/StructuredTableConstants';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {isTableCollapsed} from 'notebook/tables/collapse/useTableCollapse';
import {EvaluatedCell, FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {Table, TableData} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {toStringTypedValue} from 'notebook/tables/typed-values/DataTypes';
import TransferTable from 'notebook/transfer-tables/TransferTable';
import {TransferTableModel} from 'notebook/transfer-tables/TransferTableConstants';
import {
  convertTransferTableToTable,
  getStoreDependenciesForTransferTable,
  mergeTableUpdateIntoTransferTable,
} from 'notebook/transfer-tables/TransferTableNotePartUtil';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {ContainerSchema, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import * as logging from 'util/Logging';
import {MakeFieldsRequired} from 'util/Types';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

function doesUserHavePermissionToViewTable(
  item: TransferTableModel
): item is TransferTableModel & {containerSchema: StorableSchema} {
  return item.containerSchema != null;
}

const TRANSFER_TABLE_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.TRANSFER_TABLE,
  typeClass: 'is-table',
  title: 'Transfer Table',
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    getItemProps(item: NotePart & {item: TransferTableModel}, index: number): TableItemProps {
      return MediocrePlugins.getTableItemProps.call(this, item, index);
    },
    getIsCollapsed(
      item: NotePart & {item: TransferTableModel},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      const rowCount = item.item.rows.length;
      const cellCount = rowCount * item.item.columns.length;
      return isTableCollapsed({
        collapseId: createCollapseId(editorProps.fileId, item.item.name),
        collapsedStateById,
        rowCount,
        cellCount,
        tableKind: null,
      });
    },
    getIsInaccessible(item: NotePart & {item: TransferTableModel}): boolean {
      return !doesUserHavePermissionToViewTable(item.item);
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: TransferTableModel},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: TableItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const transferTable: TransferTableModel = item.item;
      const {name, transferOperationListId, parentStorableIsGrid, columns, rows, provenanceUuid} =
        transferTable;
      const sharedProps: MakeFieldsRequired<BasicTableProps, 'tableTypeLabel'> = {
        icon: 'transfer',
        indentation: this.getIndentation(item),
        tableTypeLabel: 'Transfer',
        tableName: name,
      };
      if (!doesUserHavePermissionToViewTable(transferTable)) {
        return <TableWithMissingSchema {...sharedProps} />;
      }
      return (
        <TransferTable
          {...sharedProps}
          editor={this}
          editorProps={editorProps}
          itemProps={itemProps}
          containerSchema={transferTable.containerSchema}
          entryId={editorProps.fileId}
          parentStorableIsGrid={parentStorableIsGrid}
          registrationTables={RegistrationTableUtil.getRegistrationTablesInNote(this.props.note)}
          structuredTableColumns={columns}
          transferOperationListId={transferOperationListId}
          provenanceUuid={provenanceUuid}
          // Standard table props
          cid={cid}
          index={index}
          mouseDownHandler={this.itemMouseDownHandler}
          mouseOverHandler={this.itemMouseOverHandler}
          rows={rows}
          updateHandler={this.itemUpdateHandler}
          {...searchResultProps}
        />
      );
    },
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToText(this: NoteEditor, item: NotePart & {item: TransferTableModel}): string {
    const evaluateTables = (tableMap: FullTableMap): OutputTableMap => {
      const selectors = this.props.selectorsFromProps(this.props);
      return this.props.evaluateTables(tableMap, selectors).computedTables;
    };
    const fullTableMap = this.props.makeFullTableMap(this.props);
    const fullTable = fullTableMap[item.item.name];

    let computedTables: OutputTableMap;

    // If Persist Eval flags are active, we will use the note runtime snapshot's evaluation.
    // Otherwise, perform evaluation here.
    if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
      computedTables = this.props.computedTables;
      // In practice, a case where the snapshot's `computedTables` isn't populated should never occur.
      // We can expect the user won't copy the table that quickly.
      // If it does occur, we'll return a table of empty cells.
      if (!computedTables.hasOwnProperty(item.item.name)) {
        logging.warn('User copied transfer table before evaluation was complete.');
        return TableUtil.tableEditableToText(
          fullTable,
          fullTable.table.map((row) =>
            row.map((cell) => ({value: toStringTypedValue(''), locked: false}) as EvaluatedCell)
          )
        );
      }
    } else {
      computedTables = evaluateTables(fullTableMap);
    }

    const computedTable = computedTables[item.item.name];
    return TableUtil.tableEditableToText(fullTable, computedTable);
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: TransferTableModel}): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    const evaluateTables = (tableMap: FullTableMap): OutputTableMap => {
      const selectors = this.props.selectorsFromProps(this.props);
      return this.props.evaluateTables(tableMap, selectors).computedTables;
    };
    const fullTableMap = this.props.makeFullTableMap(this.props);
    const fullTable = fullTableMap[item.item.name];

    let computedTables: OutputTableMap;

    // If Persist Eval flags are active, we will use the note runtime snapshot's evaluation.
    // Otherwise, perform evaluation here.
    if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
      computedTables = this.props.computedTables;
      // In practice, a case where the snapshot's `computedTables` isn't populated should never occur.
      // We can expect the user won't copy the table that quickly.
      // If it does occur, we'll return a table of empty cells.
      if (!computedTables.hasOwnProperty(item.item.name)) {
        logging.warn('User copied transfer table before evaluation was complete.');
        return $(
          $.parseHTML(
            TableUtil.tableEditableToText(
              fullTable,
              fullTable.table.map((row) =>
                row.map((cell) => ({value: toStringTypedValue(''), locked: false}) as EvaluatedCell)
              )
            )
          )
        );
      }
    } else {
      computedTables = evaluateTables(fullTableMap);
    }

    const computedTable = computedTables[item.item.name];
    $el.append(TableUtil.tableEditableToHTML(fullTable, computedTable));
    return $el;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: TransferTableModel}): NotePart | null {
    if ($el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId) {
      notifications.warn('Cannot paste transfer tables between entries.');
      return null;
    }

    if (
      TransferTableUtil.getTransferTablesInNote(this.props.note)
        .map((transferTable) => transferTable.transferOperationListId)
        .includes(item.item.transferOperationListId)
    ) {
      notifications.warn('Cannot create more than one copy of the same transfer table.');
      return null;
    }
    return item;
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: TransferTableModel}): string {
    return item.name;
  },
  getContent(
    this: NoteEditor,
    {item}: NotePart & {item: TransferTableModel & {containerSchema: ContainerSchema}}
  ): {
    // Must return a Table, not just TableData, because TableOps.deleteRows needs to check table.allowNoRows
    table: Table;
  } {
    // It's OK to not have a store dependency for the stage runs, because changing stage runs always refreshes
    // the workflow entry.
    const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
    const stageRuns = workflowSelector.getOrderedStageRunsForEntry(this.props.fileId);
    return {
      table: convertTransferTableToTable(item, stageRuns),
    };
  },
  setContent({item, table}: {item?: TransferTableModel; table?: TableData}): Spec<NotePart> {
    if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(transferTable: TransferTableModel): TransferTableModel {
            return mergeTableUpdateIntoTransferTable(transferTable, table);
          },
        },
      };
    }

    assert(false, 'setContent invoked without content to set.');
  },
  getStoreDependenciesForSpreadsheetEval({item}: NotePart & {item: TransferTableModel}): StoreDependencyMap {
    return getStoreDependenciesForTransferTable(item);
  },

  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    const transferTable = item.item as TransferTableModel;
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, transferTable.name, item.cid);

    if (!isCollapsed) {
      // Search over the table column names
      const columnNames = transferTable.columns.map((column) => column.name ?? '');
      yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

      const computedTable = computedTables?.[transferTable.name];
      assert(computedTable != null, 'Computed table not found in plugin', {
        computedTableNames: computedTables ? Object.keys(computedTables) : [],
      });
      for (const [i, row] of transferTable.rows.entries()) {
        for (const [j, cell] of row.cells.entries()) {
          if (doesTableCellMatch(matcher, cell, computedTable[i][j], searchOverFormulas)) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default TRANSFER_TABLE_PLUGIN;
